'use client';

import { useState } from 'react';
import { Box, Avatar, Typography, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import { Dashboard, Person, Security, Logout, AdminPanelSettings } from '@mui/icons-material';
import Link from 'next/link';
import type { Session } from 'next-auth';
import { logoutAction } from 'app/actions/logout';
interface UserMenuProps {
  session: Session;
  isMobile?: boolean;
  makerMarketplaceEnabled?: boolean;
}

export function UserMenu({
  session,
  isMobile,
  makerMarketplaceEnabled = true,
}: UserMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const menuItems = [
    { label: 'Dashboard', href: '/dashboard', icon: Dashboard, enabled: makerMarketplaceEnabled },
    { label: 'Profile', href: '/profile', icon: Person, enabled: makerMarketplaceEnabled },
    {
      label: 'Account Security',
      href: '/settings/security',
      icon: Security,
      enabled: makerMarketplaceEnabled,
    },
    {
      label: 'Admin Dashboard',
      href: '/admin',
      icon: AdminPanelSettings,
      enabled: session.user.role === 'ADMIN',
    },
    { label: 'Log out', onClick: logoutAction, icon: Logout, enabled: true, key: 'logout' },
  ];

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  if (isMobile) {
    return (
      <Box component="div">
        <Box sx={{ px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {session.user.name?.charAt(0)}
            </Avatar>
            <Typography variant="subtitle1">{session.user.name}</Typography>
          </Box>
        </Box>
        <Divider />
        {menuItems.map(item => (
          <MenuItem
            key={item.href || item.key}
            component={item.href ? Link : 'button'}
            href={item.href || ''}
            onClick={handleClose}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              <item.icon fontSize="small" />
            </ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box
        onClick={handleMenu}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main' }}>{session.user.name?.charAt(0)}</Avatar>
        <Typography variant="body1" color="inherit">
          {session.user.name}
        </Typography>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            minWidth: 200,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems.map(
          item =>
            item.enabled && (
              <MenuItem
                key={item.href || item.key}
                component={item.href ? (Link as any) : 'a'}
                href={item.href || undefined}
                onClick={item.onClick}
                sx={{ py: 1 }}
              >
                <ListItemIcon>
                  <item.icon fontSize="small" />
                </ListItemIcon>
                {item.label}
              </MenuItem>
            )
        )}
      </Menu>
    </>
  );
}
